import React from 'react';

import styles from './global-distribution-tile.module.scss';

type DistributionRow = {
  fournisseurs: number,
  magasins: number,
  collaborateurs: number,
};

type Props = {
  title: string,
  rows: {
    j1Only: DistributionRow,
    j2Only: DistributionRow,
    j1j2: DistributionRow,
    total: DistributionRow,
    visiteursUniquesTotal: number,
  },
  /** Affiche Total J1 et Total J2 (tous types) dans le bloc Total. */
  showDayTotals?: boolean,
};

const getFlexWeight = (value: number) => Math.max(1, value);

const sumDistributionRow = (row: DistributionRow) => (
  row.fournisseurs + row.magasins + row.collaborateurs
);

const mergeDistributionRows = (a: DistributionRow, b: DistributionRow): DistributionRow => ({
  fournisseurs: a.fournisseurs + b.fournisseurs,
  magasins: a.magasins + b.magasins,
  collaborateurs: a.collaborateurs + b.collaborateurs,
});

const lineConfig = [
  { key: 'j1Only', label: 'J1' },
  { key: 'j2Only', label: 'J2' },
  { key: 'j1j2', label: 'J1+J2' },
  { key: 'total', label: 'Total' },
] as const;

const legendConfig = [
  { key: 'fournisseurs', label: 'Fournisseurs', className: 'legendDotFournisseurs' },
  { key: 'magasins', label: 'Magasins', className: 'legendDotMagasins' },
  { key: 'collaborateurs', label: 'Collaborateurs', className: 'legendDotCollaborateurs' },
] as const;

export default function GlobalDistributionTile({ title, rows, showDayTotals = false }: Props) {
  const totalJ1 = sumDistributionRow(mergeDistributionRows(rows.j1Only, rows.j1j2));
  const totalJ2 = sumDistributionRow(mergeDistributionRows(rows.j2Only, rows.j1j2));

  return (
    <section className={styles.tile}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.body}>
        <div className={styles.chartArea}>
          <div className={styles.legend}>
            {legendConfig.map((item) => (
              <div key={item.key} className={styles.legendItem}>
                <span className={styles[item.className]} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className={styles.lines}>
            {lineConfig.map((line) => {
              const row = rows[line.key];
              const isTotal = line.key === 'total';
              return (
                <div key={line.key} className={isTotal ? `${styles.lineRow} ${styles.totalRow}` : styles.lineRow}>
                  <span className={styles.lineLabel}>
                    {line.key === 'j1Only' && <span className={styles.pillJ1}>{line.label}</span>}
                    {line.key === 'j2Only' && <span className={styles.pillJ2}>{line.label}</span>}
                    {line.key === 'j1j2' && <span className={styles.pillJ1J2}>{line.label}</span>}
                    {line.key === 'total' && line.label}
                  </span>
                  <div className={isTotal ? styles.lineValues : styles.lineBars}>
                    <div
                      className={isTotal ? styles.valueOnly : styles.segmentFournisseurs}
                      style={isTotal ? undefined : { flexGrow: getFlexWeight(row.fournisseurs) }}
                    >
                      {row.fournisseurs}
                    </div>
                    <div
                      className={isTotal ? styles.valueOnly : styles.segmentMagasins}
                      style={isTotal ? undefined : { flexGrow: getFlexWeight(row.magasins) }}
                    >
                      {row.magasins}
                    </div>
                    <div
                      className={isTotal ? styles.valueOnly : styles.segmentCollaborateurs}
                      style={isTotal ? undefined : { flexGrow: getFlexWeight(row.collaborateurs) }}
                    >
                      {row.collaborateurs}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.totalBlock}>
          <p className={styles.totalValue}>Total {rows.visiteursUniquesTotal}</p>
          <p className={styles.totalLabel}>Visiteurs uniques</p>
          {showDayTotals && (
            <div className={styles.dayTotals}>
              <p className={styles.dayTotalItem}>Total J1 : {totalJ1}</p>
              <p className={styles.dayTotalItem}>Total J2 : {totalJ2}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
